import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["open", "answered", "closed", ""];

export default function AdminTickets() {
  const client = adminClient();
  const [filter, setFilter] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    client.get("/admin/tickets", { params: { status: filter || undefined } }).then(({ data }) => setTickets(data.tickets)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const open = async (tid) => { try { const { data } = await client.get(`/admin/tickets/${tid}`); setSelected(data.ticket); } catch { toast.error("Failed"); } };
  const send = async () => { if (!reply.trim()) return; try { await client.post(`/admin/tickets/${selected.ticket_id}/reply`, { message: reply }); setReply(""); open(selected.ticket_id); load(); } catch { toast.error("Failed"); } };
  const setStatus = async (status) => { try { await client.put(`/admin/tickets/${selected.ticket_id}`, { status }); toast.success("Updated"); open(selected.ticket_id); load(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="admin-tickets">
      <h2 className="text-xl font-black font-display mb-4">Support Tickets</h2>
      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => <button key={f || "all"} onClick={() => { setFilter(f); setSelected(null); }} data-testid={`ticket-filter-${f || "all"}`} className={`text-xs font-bold uppercase tracking-wider px-3 py-2 ${filter === f ? "bg-taven-black text-white" : "bg-taven-gray"}`}>{f || "all"}</button>)}
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        <div className="bg-white border border-black/10 max-h-[70vh] overflow-y-auto">
          {loading ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div> :
            tickets.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No tickets.</p> :
              tickets.map((t) => (
                <button key={t.ticket_id} onClick={() => open(t.ticket_id)} data-testid="admin-ticket-row" className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-taven-gray/40 ${selected?.ticket_id === t.ticket_id ? "bg-taven-gray/60" : ""}`}>
                  <div className="flex items-center justify-between"><p className="font-bold text-sm truncate">{t.subject}</p><span className="text-[10px] uppercase font-bold">{t.status}</span></div>
                  <p className="text-xs text-muted-foreground">{t.name} · {t.email}</p>
                  <p className="text-[10px] text-muted-foreground">{t.ticket_id} · {t.category}</p>
                </button>
              ))}
        </div>

        <div className="bg-white border border-black/10 p-5">
          {!selected ? <p className="text-sm text-muted-foreground">Select a ticket to view the conversation.</p> :
            <div data-testid="admin-ticket-detail">
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-black/10 pb-3">
                <div><p className="font-bold">{selected.subject}</p><p className="text-xs text-muted-foreground">{selected.ticket_id} · {selected.email} {selected.order_number && `· Order ${selected.order_number}`}</p></div>
                <select value={selected.status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-status-select" className="border border-black/15 px-2 py-1 text-xs">{["open", "answered", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
              </div>
              <div className="space-y-3 py-4 max-h-[45vh] overflow-y-auto">
                {selected.messages.map((m, i) => (
                  <div key={i} className={`max-w-[85%] px-3.5 py-2.5 text-sm ${m.from === "support" ? "bg-taven-black text-white ml-auto" : "bg-taven-gray"}`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">{m.author}</p>{m.body}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 border-t border-black/10 pt-3">
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…" data-testid="admin-reply-input" className="flex-1 border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
                <button onClick={send} data-testid="admin-reply-send" className="bg-taven-orange text-white p-2.5 hover:bg-[#d9561a]"><Send className="w-4 h-4" /></button>
              </div>
            </div>}
        </div>
      </div>
    </div>
  );
}
